/**
 * «Библиотеки»: склад референсов, сгруппированный по категории — интерьеры
 * отдельно, действия отдельно, образы отдельно. Это и есть смысл категории.
 *
 * Референс — то, на что опираемся, а не то, что снимаем: он переживает проект,
 * поэтому живёт здесь, а не в проекте. Пины трендов попадают сюда же.
 *
 * «Поиск» (по описанию, фото, платью) — `PhotoSearchPanel` с выбором библиотеки (как на «Всех референсах»):
 * есть выдача — вместо полок категорий её плитка.
 */
import { useQuery } from "@tanstack/react-query";
import { ImageOff, Plus, ScanSearch } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { LibNav } from "@/components/library/LibNav";
import { LibraryDialog } from "@/components/library/LibraryDialog";
import { PhotoSearchPanel, type SimilarFound } from "@/components/library/PhotoSearchPanel";
import { ReferenceBrowser } from "@/components/library/ReferenceBrowser";
import { btn, btnPrimary, Empty, ErrorLine, Loading, Panel } from "@/components/trends/ui";
import { libraryKeys, loadLibraries, type LibraryCard } from "@/lib/library";
import { mediaUrl, num } from "@/lib/media";
import { scoreMap, showsPercent } from "@/lib/similar";
import { retryQuery } from "@/lib/trends";

export function LibrariesPage() {
  const navigate = useNavigate();
  const list = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries, retry: retryQuery });
  const [adding, setAdding] = useState(false);
  // Ссылка с `?similar_dress=` / `?similar_text=` открывает экран сразу с поиском.
  const [params] = useSearchParams();
  const [searchOpen, setSearchOpen] = useState(() => params.has("similar_dress") || params.has("similar_text"));
  const [found, setFound] = useState<SimilarFound | null>(null);
  const scores = useMemo(() => (found ? scoreMap(found.view.items) : null), [found]);

  if (list.isPending) return <Loading />;
  if (list.isError)
    return (
      <div className="p-5">
        <ErrorLine error={list.error} />
      </div>
    );

  const { groups } = list.data;

  return (
    <div className="grid gap-4 p-5">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Библиотеки референсов</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            На что мы опираемся, а не что снимаем: интерьеры, действия, образы, примеры рилсов. Референс переживает
            проект, поэтому лежит здесь.
          </p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Link to="/lib/interiors" className={btn}>
            Интерьеры: оценки
          </Link>
          {!searchOpen ? (
            <button type="button" className={btnPrimary} onClick={() => setSearchOpen(true)}>
              <ScanSearch size={14} />
              Поиск
            </button>
          ) : null}
          <button type="button" className={btnPrimary} onClick={() => setAdding(true)}>
            <Plus size={14} />
            Добавить библиотеку
          </button>
        </div>
      </div>

      <LibNav current="list" />

      {searchOpen ? <PhotoSearchPanel onResult={setFound} onClose={() => setSearchOpen(false)} /> : null}

      {found && scores ? (
        <ReferenceBrowser
          key="similar"
          items={found.view.items}
          mode="all"
          queryKey={found.queryKey}
          resetKey={JSON.stringify(found.queryKey)}
          scores={scores}
          percent={showsPercent(found.view)}
          title={
            <>
              {showsPercent(found.view) ? "Похожие" : "Найдено"} {found.label} <span className="tnum text-fg-faint">{num(found.view.items.length)}</span>
            </>
          }
          emptyText="Похожих не нашлось."
        />
      ) : groups.length === 0 ? (
        <Panel annot="библиотеки">
          <Empty>Библиотек пока нет — нажмите «Добавить библиотеку».</Empty>
        </Panel>
      ) : (
        groups.map((g) => (
          <Panel
            key={g.code}
            title={
              <>
                {g.name} <span className="tnum font-normal text-fg-faint">{g.libs.length}</span>
              </>
            }
            annot={`категория ${g.code}`}
          >
            <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3 p-3">
              {g.libs.map((lib) => (
                <LibraryTile key={lib.id} lib={lib} />
              ))}
            </div>
          </Panel>
        ))
      )}

      <LibraryDialog open={adding} onClose={() => setAdding(false)} onDone={(id) => navigate(`/lib/${id}`)} />
    </div>
  );
}

function LibraryTile({ lib }: { lib: LibraryCard }) {
  return (
    <Link
      to={`/lib/${lib.id}`}
      className="group grid overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle transition-colors hover:border-border-strong"
      data-annot="библиотека"
    >
      <div className="aspect-[4/5] overflow-hidden bg-bg-elevated">
        {lib.cover ? (
          <img
            src={mediaUrl(lib.cover)}
            alt={lib.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="grid h-full place-items-center text-[12px] text-fg-faint">
            <span className="grid justify-items-center gap-1">
              <ImageOff size={18} />
              пусто
            </span>
          </div>
        )}
      </div>
      <div className="grid gap-0.5 px-2.5 py-2">
        <div className="truncate text-[13px] font-medium text-fg group-hover:text-accent-text" title={lib.name}>
          {lib.name}
        </div>
        <div className="text-[11.5px] text-fg-muted tnum">{num(lib.photos)} фото</div>
        {lib.note ? (
          <div className="line-clamp-2 text-[11.5px] text-fg-faint" title={lib.note}>
            {lib.note}
          </div>
        ) : null}
      </div>
    </Link>
  );
}
